import React from 'react'
import IconWithBackground from '../IconWithBackground/IconWithBackground'
import { OrderDetailResponse, OrderLine, Product } from '../../model'
import { formatDate } from '../../helpers/utils'
import { formatCurrencyValue } from '../../helpers/utils'
import ProductItem from '../ProductItem/ProductItem'
import IconInCircle from '../IconInCircle/IconInCircle'

interface OrderDetailHeaderProps {
    order: OrderDetailResponse
}

const OrderDetailHeader: React.FC<OrderDetailHeaderProps> = ({ order }) => {
    const address = order.address
    const thirdPartyInvoiceDetails = order.thirdPartyInvoiceDetails
    const products = order.products ?? []
    const packedProducts = products.filter((p: OrderLine) => p.isPartOfPack)
    const otherProducts = products.filter((p: OrderLine) => !p.isPartOfPack)

    const renderProductGrid = (items: OrderLine[]) => (
        <div
            className='w-full md:auto-cols-fr grid md:[grid-template-rows:repeat(var(--product-rows),auto)] md:grid-flow-col gap-4'
            style={{ '--product-rows': Math.ceil(items.length / 2) } as React.CSSProperties}
        >
            {items.map((product: OrderLine) => (
                <div
                    className={`border rounded-lg bg-white shadow-sm !p-0 !py-1 w-full !items-start ${items.length < 2 ? 'max-w-md' : ''}`}
                    key={product.id}
                >
                    <ProductItem
                        product={product as Product}
                        showOptions
                        size='medium'
                        className='!border-0 !py-2'
                        iconLocation='start'
                        colors={{
                            iconColor: 'text-[--tb-primary]',
                            iconBgColor: 'bg-[--tb-blushPrimary]'
                        }}
                    />
                </div>
            ))}
        </div>
    )

    return (
        <div className='w-full'>
            <h2 className='text-base'>Opdracht</h2>
            <div className='w-full bg-white border rounded-xl shadow-sm p-4'>
                <div className='grid items-center lg:items-start flex-grow grid-cols-12 gap-y-2 gap-x-3 lg:gap-x-4 w-full'>
                    <div className='col-span-full sm:col-span-5 lg:col-span-5 flex items-center space-x-3'>
                        <div>
                            <div className='flex items-center space-x-3 w-full min-w-0'>
                                <IconWithBackground iconName='house' />
                                <div className='flex flex-col'>
                                    <span className='font-semibold text-sm truncate text-wrap'>
                                        {address.street} {address.houseNumber}
                                        {address.extension && ` (${address.extension})`}
                                    </span>
                                    <span className='text-xs font-medium text-[--tb-textLight]'>
                                        {address.zipcode} {address.city}
                                    </span>
                                    {order.surface && (
                                        <span className='text-xs text-[--tb-primary]'>{order.surface} m2</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='flex justify-between items-center my-2 sm:my-0 flex-wrap gap-4 col-span-full sm:col-span-5 lg:col-span-4'>
                        {order.pack ? (
                            <div className='w-full max-w-xs'>
                                <ProductItem
                                    product={order.pack}
                                    showOptions={false}
                                    size='small'
                                    orderDate={order.orderDate}
                                    className='px-0 py-0 !border-0 !items-start'
                                />
                            </div>
                        ) : (
                            <div className='flex space-x-3 items-center'>
                                <IconWithBackground size='small' iconName={'calendar'} />

                                <div className='flex flex-col text-sm justify-center w-full min-w-0'>
                                    {order.orderDate && (
                                        <>
                                            <strong className='mr-2 font-semibold'>Besteld:</strong>
                                            <span className='text-[--tb-textLight]'>{formatDate(order.orderDate)}</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Price */}
                    <div className='col-span-full sm:col-span-2 lg:col-span-3 flex flex-col justify-end text-sm text-left sm:text-right'>
                        <span className='text-[--tb-textLight] text-base'>Totaal</span>
                        {order.totalPrice ? (
                            <>
                                <span className='font-semibold'>{formatCurrencyValue(order.totalPrice)}</span>
                                <span className='text-xs text-[--tb-textLight]'> incl. producten en opties</span>
                            </>
                        ) : (
                            '-'
                        )}
                    </div>
                </div>
            </div>
            <div className='flex w-full flex-col pt-4'>
                <div className='relative w-full mx-auto bg-white p-4 rounded-xl border-full shadow-sm flex flex-col gap-4'>
                    <h3 className='-mb-2'>Jouw producten</h3>
                    {order.products?.length ? (
                        <>
                            {packedProducts.length > 0 && (
                                <div className='rounded-lg border border-[--tb-primary]/30 bg-tibi-lightGray/10 p-3 flex flex-col gap-3'>
                                    <div className='flex items-center justify-between gap-3'>
                                        <div className='flex items-center gap-3'>
                                            <IconWithBackground size='small' iconName='box-taped' />
                                            <div className='flex flex-col'>
                                                <span className='text-sm font-semibold'>
                                                    {order.pack ? `${order.pack.title}` : 'Pakket'}
                                                </span>
                                                <span className='text-xs text-[--tb-textLight]'>
                                                    {packedProducts.length} producten inbegrepen · opties met + zijn
                                                    apart berekend
                                                </span>
                                            </div>
                                        </div>
                                        {order.pack && (
                                            <span className='text-sm font-semibold shrink-0'>
                                                {formatCurrencyValue(order.pack.price)}
                                            </span>
                                        )}
                                    </div>
                                    {renderProductGrid(packedProducts)}
                                </div>
                            )}
                            {otherProducts.length > 0 && (
                                <div className='flex flex-col gap-3'>
                                    {packedProducts.length > 0 && (
                                        <h4 className='text-sm font-semibold'>Overige producten</h4>
                                    )}
                                    {renderProductGrid(otherProducts)}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className='max-w-md text-xs px-4 text-[--tb-textLight]'>Geen producten</div>
                    )}
                    {order.remarks && (
                        <div className='text-xs bg-tibi-lightGray/15 py-2 px-2 w-full rounded-md border'>
                            <strong>Opmerking:</strong> {order.remarks}
                        </div>
                    )}
                </div>
            </div>
            {thirdPartyInvoiceDetails && (
                <div className='flex w-full flex-col mt-4'>
                    <h3 className='mb-2'>Factureren aan</h3>
                    <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-20 bg-white rounded-xl border-full shadow-sm p-4'>
                        <div className='flex items-center gap-3'>
                            <IconInCircle
                                iconName='receipt'
                                borderColor='borderGreenColor'
                                textColor='text-[--tb-green]'
                                bgColor='bg-white'
                            />
                            <div className='flex flex-col text-sm'>
                                <span>
                                    {address.street} {address.houseNumber}
                                    {address.extension && ' (' + address.extension + ')'}
                                </span>
                                <span>
                                    {address.zipcode} {address.city}
                                </span>
                            </div>
                        </div>
                        <div className='flex items-center gap-2'>
                            <IconInCircle
                                iconName='user'
                                size='small'
                                borderColor='borderGreenColor'
                                textColor='text-[--tb-green]'
                                bgColor='bg-white'
                            />
                            <div className='flex flex-col text-xs'>
                                {thirdPartyInvoiceDetails.name && (
                                    <span className='font-semibold text-sm'>{thirdPartyInvoiceDetails.name}</span>
                                )}
                                {thirdPartyInvoiceDetails.emailAddress && (
                                    <span className='text-[--tb-textLight]'>
                                        {thirdPartyInvoiceDetails.emailAddress}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default OrderDetailHeader
